import { useState, useEffect } from 'react';
import { Trash2, Download } from 'lucide-react';
import { haalLogs, wisLogsApi, markeerLogsGezien, LOGS_GEZIEN_EVENT } from '../lib/api.js';
import { bestandsstempelNL } from '../lib/tijd.js';

// Badge-kleur per niveau (zelfde idioom als TYPE_STIJL in Beheer.jsx)
const LEVEL_STIJL = {
  error: 'bg-red-100 text-red-800',
  warn: 'bg-amber-100 text-amber-800',
  info: 'bg-slate-100 text-slate-700',
};

const FILTERS = [
  { key: 'alle', label: 'Alle niveaus' },
  { key: 'error', label: 'Alleen error' },
  { key: 'warn', label: 'Alleen warn' },
  { key: 'info', label: 'Alleen info' },
];

// Sorteersleutel: het moment van optreden, met de server-ontvangst als terugval.
// `timestamp` komt van de CLIENT en wordt server-side alleen op "is een string" gekeurd,
// dus een onbruikbare waarde is mogelijk. Die zakt naar onderen (0 = 1970) in plaats van
// een NaN in de comparator te lekken, want een NaN maakt de hele sortering onvoorspelbaar.
function sorteerTijd(e) {
  const t = Date.parse(e?.timestamp || '');
  if (!Number.isNaN(t)) return t;
  const o = Date.parse(e?.ontvangenOp || '');
  return Number.isNaN(o) ? 0 : o;
}

function formatTijd(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('nl-NL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

export default function LogViewer() {
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState('alle');
  const [laden, setLaden] = useState(true);
  const [fout, setFout] = useState(null);
  const [herlaad, setHerlaad] = useState(0); // ophogen = opnieuw laden (Vernieuwen / na Wis)

  // Server is de bron: dit toont de fouten van ALLE gebruikers (teamleider-only endpoint),
  // met per item wie het was, wanneer, op welke pagina en hoe vaak. Idioom = MijnRooster.jsx:
  // de async laad-functie staat IN het effect, wat react-hooks/set-state-in-effect tevreden houdt.
  useEffect(() => {
    let geannuleerd = false;
    const laad = async () => {
      setLaden(true);
      setFout(null);
      try {
        const res = await haalLogs();
        if (!geannuleerd) setEntries(Array.isArray(res?.logs) ? res.logs : []);
        // Het logboek staat nu écht open → stempelen dat DEZE teamleider het gezien heeft,
        // zodat zijn teller bij Beheer op nul gaat (die van een andere teamleider blijft
        // staan). Bewust NA een geslaagde ophaal: mislukt het laden, dan heb je niets gezien
        // en hoort er ook geen stempel te staan. Eigen try, want stil falen is hier de eis —
        // een mislukt stempel mag het logboek niet blokkeren; de volgende keer stempelt het
        // gewoon opnieuw. Het event zet de nav-badge direct op nul: het logboek is een TAB
        // binnen Beheer, dus er is geen navigatie waar de LogBadgeWatcher op kan afgaan.
        try {
          await markeerLogsGezien();
          window.dispatchEvent(new CustomEvent(LOGS_GEZIEN_EVENT));
        } catch { /* stil: stempelen is bijzaak, de lijst tonen is de hoofdzaak */ }
      } catch (err) {
        if (!geannuleerd) setFout(err?.message || 'Het logboek kon niet worden geladen.');
      } finally {
        if (!geannuleerd) setLaden(false);
      }
    };
    laad();
    return () => { geannuleerd = true; };
  }, [herlaad]);

  // Sorteren op HETZELFDE veld dat hieronder op het scherm komt: `timestamp`, het moment
  // waarop de fout OPTRAD. De server sorteert op `ontvangenOp`, het moment van BINNENKOMST;
  // dat is voor de bewaartermijn en de nieuw-teller het juiste veld, maar niet voor een
  // teamleider die wil weten wanneer er iets misging. Normaal schelen ze ~2s (de debounce
  // van logSync), maar een fout die zonder token blijft wachten — inlogscherm, offline —
  // gaat pas mee zodra er wél een token is, en staat dan op een plek die niet klopt met de
  // tijd die ernaast staat. filter() geeft al een nieuwe array, dus sort() raakt `entries` niet.
  const zichtbaar = entries
    .filter((e) => filter === 'alle' || e.level === filter)
    .sort((a, b) => sorteerTijd(b) - sorteerTijd(a));

  async function wis() {
    if (!window.confirm('Het logboek van ALLE gebruikers echt wissen? Dit kan niet ongedaan worden gemaakt.')) return;
    setFout(null);
    try {
      await wisLogsApi();
      setHerlaad((n) => n + 1); // opnieuw laden toont de nu-lege lijst
    } catch (err) {
      setFout(err?.message || 'Wissen is mislukt.');
    }
  }

  // Exporteer wat er nú getoond wordt (de gefilterde server-items) als JSON-bestand.
  function exporteer() {
    try {
      const content = JSON.stringify(zichtbaar, null, 2);
      // bestandsstempelNL, niet toISOString: die noemt een export van 00:05 naar gisteren
      // en zet er het UTC-uur bij — zie lib/tijd.js.
      const stamp = bestandsstempelNL();
      const blob = new Blob([content], { type: 'application/json' });
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `sass-rooster-log-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      setFout('Exporteren is mislukt.');
    }
  }

  return (
    <div className="space-y-4">
      {/* Actiebalk: filter links, knoppen rechts */}
      <div className="flex justify-between items-center gap-3">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          {FILTERS.map((f) => (
            <option key={f.key} value={f.key}>{f.label}</option>
          ))}
        </select>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setHerlaad((n) => n + 1)}
            className="px-3 py-2 text-xs text-slate-500 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:text-slate-700 transition"
          >
            Vernieuwen
          </button>
          <button
            onClick={exporteer}
            className="flex items-center gap-1.5 px-3 py-2 text-xs text-slate-500 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:text-slate-700 transition"
          >
            <Download className="w-3.5 h-3.5" />
            Exporteer log
          </button>
          <button
            onClick={wis}
            className="flex items-center gap-1.5 px-3 py-2 text-xs text-slate-500 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:text-red-600 transition"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Wis log
          </button>
        </div>
      </div>

      {/* Foutregel — nooit stil wegslikken */}
      {fout && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
          {fout}
        </div>
      )}

      {/* Lijst / laad- / lege staat */}
      {laden ? (
        <div className="text-center py-8 text-slate-400 text-sm">Logboek laden…</div>
      ) : zichtbaar.length === 0 ? (
        <div className="text-center py-8 text-slate-400 text-sm">
          {entries.length === 0 ? 'Het logboek is leeg.' : 'Geen regels voor dit filter.'}
        </div>
      ) : (
        <div className="space-y-2">
          {zichtbaar.map((e) => (
            <div key={e.id} className="bg-white rounded-xl border border-slate-200 px-5 py-4">
              {/* Kopregel: niveau, source, wie, ×N, tijd */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${LEVEL_STIJL[e.level] || LEVEL_STIJL.info}`}>
                  {e.level}
                </span>
                <span className="text-xs text-slate-400">{e.source}</span>
                {e.naam && (
                  <span className="text-xs font-medium text-slate-600">{e.naam}</span>
                )}
                {e.count > 1 && (
                  <span className="text-xs font-medium text-slate-500">×{e.count}</span>
                )}
                <span className="text-xs text-slate-400 ml-auto">{formatTijd(e.timestamp)}</span>
              </div>

              {/* Bericht */}
              <div className="mt-2 text-sm text-slate-700 break-words">{e.message}</div>

              {/* Pagina waar het gebeurde */}
              {e.url && (
                <div className="mt-1 text-xs text-slate-400 break-all">{e.url}</div>
              )}

              {/* Stacktrace — alleen als aanwezig, inklapbaar */}
              {e.stack && (
                <details className="mt-2">
                  <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-600 select-none">
                    Stacktrace
                  </summary>
                  <pre className="mt-1 text-xs text-slate-500 bg-slate-50 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words">
                    {e.stack}
                  </pre>
                </details>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
